import {
  Archive,
  AudioLines,
  BookOpen,
  ChartSpline,
  Code2,
  FileScan,
  FolderSync,
  ListChecks,
  Map,
  PenTool,
  ScanLine,
  Waves,
} from 'lucide-react'
import artifactScanning from '../assets/images/3d-artifact-scanning.webp'
import audioVideoDigitization from '../assets/images/audio-video-digitization.webp'
import dataConversion from '../assets/images/data-conversion.webp'
import documentDigitization from '../assets/images/document-digitization.webp'
import dgfloLogo from '../assets/images/dgflo.png'
import gisServices from '../assets/images/gis-services.webp'
import largeFormatScanning from '../assets/images/large-format-scanning.webp'
import ocrSearchablePdf from '../assets/images/ocr-searchable-pdf.webp'
import seismicVectorization from '../assets/images/seismic-data-vectorization.webp'
import documentStudio from '../assets/images/service-document-studio.png'
import informationSystems from '../assets/images/service-information-systems.png'
import wellLogVectorization from '../assets/images/well-log-vectorization.webp'
import type { Service } from '../types'

const captureFlow = ['Review the source material', 'Agree capture and indexing requirements', 'Process and review the output']
const conversionFlow = ['Assess the available source', 'Define the required digital structure', 'Convert, review and prepare delivery']
const solutionFlow = ['Review the current requirement', 'Define the proposed approach', 'Agree implementation and support']

export const services: Service[] = [
  {
    slug: 'high-volume-document-scanning-indexing',
    title: 'High Volume Document Scanning and Indexing',
    category: 'Digitization',
    summary: 'Scanning and indexing for substantial physical document collections requiring organized digital delivery.',
    description: 'This service brings together document preparation, scanning, indexing and delivery in an agreed digital format. Source condition, volume, indexing structure and intended access requirements are defined for each project.',
    outcomes: ['Reduced reliance on physical document handling', 'More organized retrieval through agreed indexing', 'Digital access to physical records'],
    deliverables: ['Supported source sizes and formats', 'Capture and image-quality requirements', 'Indexing fields, output format and delivery structure'],
    applications: ['Administrative files', 'Historical records', 'Operational document collections'],
    steps: captureFlow,
    image: documentDigitization,
    imageAlt: 'Document digitization operator preparing business records for a production scanner',
    icon: FileScan,
    featured: true,
  },
  {
    slug: 'professional-indexing-data-capture',
    title: 'Professional Indexing & Data Capture',
    category: 'Data Conversion',
    summary: 'Structured indexing and data capture for turning suitable source information into organized digital records.',
    description: 'Professional indexing and data capture support the organization of suitable document information for retrieval, migration or an agreed destination. Fields, validation requirements and exception handling are established before processing.',
    outcomes: ['Consistent digital information structure', 'Support for search and retrieval', 'Data prepared for an agreed operational use'],
    deliverables: ['Required indexing fields', 'Data-capture and validation rules', 'Output structure and handover method'],
    applications: ['Document indexes', 'Metadata capture', 'Structured information preparation'],
    steps: conversionFlow,
    image: ocrSearchablePdf,
    imageAlt: 'Document-processing workstation showing scanned forms, recognized text regions and structured digital output',
    icon: ListChecks,
    featured: true,
  },
  {
    slug: 'e-book',
    title: 'E-book',
    category: 'Digitization',
    summary: 'Digital capture and preparation of bound books and publications for agreed electronic use.',
    description: 'The E-book service applies document-digitization planning to bound material. Handling, capture method, page size, condition, indexing and the required electronic output are agreed for each collection.',
    outcomes: ['More accessible electronic copies', 'Reduced routine handling of source volumes', 'Support for organized publication access'],
    deliverables: ['Accepted book sizes and condition', 'Capture and handling method', 'Electronic file, indexing and delivery format'],
    applications: ['Books and publications', 'Bound registers', 'Reference collections'],
    steps: captureFlow,
    image: documentStudio,
    imageAlt: 'Illustrative overhead book scanner capturing bound archival material',
    icon: BookOpen,
    featured: true,
  },
  {
    slug: 'audio-visual-digitization',
    title: 'Audio Visual Digitization',
    category: 'Digitization',
    summary: 'Transfer of selected legacy audio and video carriers into digital formats that are easier to access and store.',
    description: 'The available company content references VHS, Betacam, audio cassette and selected data-storage tape formats. Carrier condition, accepted formats, output requirements and review scope are confirmed for each engagement.',
    outcomes: ['More convenient access to legacy content', 'Digital files that are easier to store and share', 'Reduced reliance on obsolete playback formats'],
    deliverables: ['Carrier formats accepted', 'Digital output and transfer requirements', 'Condition assessment and quality-review scope'],
    applications: ['VHS and Betacam collections', 'Audio cassette archives', 'Selected legacy tape material'],
    steps: captureFlow,
    image: audioVideoDigitization,
    imageAlt: 'Technician operating professional video and audio tape digitization equipment',
    icon: AudioLines,
    featured: true,
  },
  {
    slug: '3d-scanning-artifacts',
    title: '3D Scanning for Artifacts',
    category: 'Digitization',
    summary: 'Three-dimensional capture for suitable artifacts based on agreed handling and digital-output requirements.',
    description: 'This service is intended for suitable artifacts that require a three-dimensional digital representation. The object type, condition, handling approach, capture scope and required output must be assessed before work begins.',
    outcomes: ['A digital three-dimensional representation of suitable material', 'Reduced handling for selected digital-access needs', 'Output aligned to an agreed purpose'],
    deliverables: ['Artifact suitability and handling requirements', 'Capture coverage and output format', 'Review, file structure and delivery method'],
    applications: ['Suitable collection artifacts', 'Object documentation', 'Digital-access preparation'],
    steps: captureFlow,
    image: artifactScanning,
    imageAlt: 'Technician using a structured-light 3D scanner to capture a ceramic artifact',
    icon: ScanLine,
  },
  {
    slug: 'gis-geospatial-services',
    title: 'GIS & Geospatial Services',
    category: 'Technology',
    summary: 'Geographic information requirements defined around each organization’s data, workflow and intended result.',
    description: 'SATSB identifies Geographic Information System services as a principal business activity. Project objectives, available information, platform requirements and expected deliverables are defined around each organization’s needs.',
    outcomes: ['Requirements aligned with the intended geographic-information use', 'A defined approach to geospatial information needs', 'Clear project scope and expected output'],
    deliverables: ['Required software or platform context', 'Spatial-data, mapping or conversion scope', 'Implementation and support responsibilities'],
    applications: ['Organizational GIS requirements', 'Geospatial information workflows', 'Spatial-data project planning'],
    steps: solutionFlow,
    image: gisServices,
    imageAlt: 'GIS analyst reviewing map layers and geographic data at a dual-monitor workstation',
    icon: Map,
    featured: true,
  },
  {
    slug: 'drawing-vectorization',
    title: 'Drawing Vectorization',
    category: 'Data Conversion',
    summary: 'Conversion of suitable scanned drawings into an agreed digital vector structure.',
    description: 'Drawing vectorization begins with the available drawing, its condition and the intended digital use. The required vector structure, layer organization, review method and output format are agreed before conversion.',
    outcomes: ['Drawings prepared for agreed digital use', 'More structured access to suitable drawing information', 'Vector output aligned to defined requirements'],
    deliverables: ['Supported source drawings', 'Required vector and layer structure', 'Review rules and output format'],
    applications: ['Technical drawings', 'Plans and diagrams', 'Suitable large-format source material'],
    steps: conversionFlow,
    image: largeFormatScanning,
    imageAlt: 'Technician positioning a technical drawing on a large-format scanner',
    icon: PenTool,
  },
  {
    slug: 'seismic-data-digitization-vectorization',
    title: 'Seismic Data Digitization and Vectorization',
    category: 'Data Conversion',
    summary: 'Digitization and vectorization of suitable seismic source material into an agreed digital structure.',
    description: 'The service is structured around the condition and format of the available seismic source material and the required digital result. Capture, vectorization, validation and delivery requirements must be confirmed for each project.',
    outcomes: ['Seismic source material prepared for agreed digital use', 'Structured output based on defined requirements', 'A documented conversion and review scope'],
    deliverables: ['Accepted source format and condition', 'Required vectorization and validation rules', 'Digital output structure and delivery method'],
    applications: ['Suitable printed seismic sections', 'Legacy seismic records', 'Geoscience information conversion'],
    steps: conversionFlow,
    image: seismicVectorization,
    imageAlt: 'Geoscience specialist converting a printed seismic section into structured digital vector traces',
    icon: Waves,
  },
  {
    slug: 'well-log-data-digitization-vectorization',
    title: 'Well-log Data Digitization and Vectorization',
    category: 'Data Conversion',
    summary: 'Digitization and vectorization of suitable well-log source records into an agreed digital structure.',
    description: 'Well-log digitization and vectorization are planned around the available source record and intended digital use. Track structure, curve capture, validation expectations and output format are defined for the engagement.',
    outcomes: ['Well-log records prepared for agreed digital use', 'Structured curves based on defined requirements', 'A clear validation and delivery scope'],
    deliverables: ['Accepted source condition and format', 'Track, curve and validation requirements', 'Output structure and delivery method'],
    applications: ['Suitable printed well logs', 'Legacy well-log records', 'Technical data conversion'],
    steps: conversionFlow,
    image: wellLogVectorization,
    imageAlt: 'Technical operator digitizing a long paper well log into structured digital curves',
    icon: ChartSpline,
  },
  {
    slug: 'document-management-integration',
    title: 'Document Management Integration',
    category: 'Information Management',
    summary: 'Integration planning intended to help organizations store, organize, search and access documents more efficiently.',
    description: 'Available company content describes document-management capabilities involving search, version control, permissions and collaboration. The target platform, integration points, deployment model and responsibilities must be defined for each implementation.',
    outcomes: ['More organized document access', 'Support for retrieval within an agreed workflow', 'Integration aligned to defined operational needs'],
    deliverables: ['Target platform and integration points', 'Document, metadata and access requirements', 'Implementation, testing and support responsibilities'],
    applications: ['Departmental document repositories', 'Shared information workflows', 'Digitized-record integration'],
    steps: solutionFlow,
    image: informationSystems,
    imageAlt: 'Illustrative enterprise document-management interface and connected digital archive',
    icon: FolderSync,
    featured: true,
  },
  {
    slug: 'dg-flo-erms',
    title: 'DG-flo Electronic Records Management System (ERMS)',
    category: 'Information Management',
    summary: 'An electronic records management system offering identified by SATSB for structured digital-record requirements.',
    description: 'DG-flo is identified by SATSB as an Electronic Records Management System offering. Functional requirements, deployment approach, integration, access controls, implementation and support must be confirmed for each organization.',
    outcomes: ['A defined approach to electronic records requirements', 'More structured access to agreed digital records', 'Implementation scope aligned to organizational needs'],
    deliverables: ['Approved functional requirements', 'Deployment and integration scope', 'Implementation, access and support responsibilities'],
    applications: ['Electronic records requirements', 'Organizational record workflows', 'Managed digital information access'],
    steps: solutionFlow,
    image: dgfloLogo,
    imageAlt: 'Official DGflo Electronic Records Management System logo',
    imageFit: 'contain',
    icon: Archive,
  },
  {
    slug: 'software-development',
    title: 'Software Development',
    category: 'Technology',
    summary: 'Software development shaped around an agreed business requirement, workflow and implementation scope.',
    description: 'Software development requirements begin with the operational need and intended result. Functional scope, integration, testing, delivery, ownership and support responsibilities must be agreed before implementation.',
    outcomes: ['Software scope aligned to a defined requirement', 'A clear implementation and review path', 'Delivery responsibilities established for the engagement'],
    deliverables: ['Approved functional requirements', 'Integration, testing and delivery scope', 'Ownership, maintenance and support responsibilities'],
    applications: ['Workflow requirements', 'Information-access needs', 'Approved business software requirements'],
    steps: solutionFlow,
    image: dataConversion,
    imageAlt: 'Technology specialist reviewing structured information across a dual-monitor workstation',
    icon: Code2,
  },
]

export const featuredServices = services.filter((service) => service.featured)

export const serviceCategories = ['Digitization', 'Data Conversion', 'Information Management', 'Technology'] as const

export const legacyServiceRedirects: Record<string, string> = {
  'document-digitization': '/services/high-volume-document-scanning-indexing',
  'book-scanning': '/services/e-book',
  'large-format-scanning': '/services/drawing-vectorization',
  'photo-scanning': '/services/professional-indexing-data-capture',
  'microfilm-microfiche-digitization': '/services/high-volume-document-scanning-indexing',
  'ocr-searchable-pdf': '/services/professional-indexing-data-capture',
  'data-conversion': '/services/professional-indexing-data-capture',
  'audiovisual-digitization': '/services/audio-visual-digitization',
  'document-records-management': '/services/document-management-integration',
  'imaging-consultation': '/services/document-management-integration',
  'geographic-information-system': '/services/gis-geospatial-services',
  'scanners-it-hardware': '/hardware',
}

export function getService(slug?: string) {
  return services.find((service) => service.slug === slug)
}
